import { useQuery } from '@tanstack/react-query';

import { CustomAttributeType } from '@/modules/common/types/custom-attribute';
import { getCustomAttributesForUsers } from '@/modules/team/api/custom-attributes.api';

/**
 * Query key for the member custom attributes settings screen.
 *
 * Shares the `'custom-attributes'` prefix with the team page hook
 * (`CUSTOM_ATTRIBUTES_USER_QUERY_KEY`) so a single predicate-based invalidation
 * refreshes both. The `'settings'` segment keeps the cache entry distinct.
 */
export const CUSTOM_ATTRIBUTES_SETTINGS_QUERY_KEY = ['custom-attributes', 'USER', 'settings'];

// The frontend only renders/edits single- and multi-select attributes for now. Other
// backend types (text, number, boolean, date) are supported server-side but their UI is
// not implemented yet, so they are filtered out here. Remove an entry once its UI lands.
const SUPPORTED_CUSTOM_ATTRIBUTE_TYPES: ReadonlySet<CustomAttributeType> = new Set([
  CustomAttributeType.Select,
  CustomAttributeType.MultiSelect,
]);

/**
 * Fetches the member custom attribute definitions for the settings screen, filtered to
 * the types the frontend currently supports (single- and multi-select).
 */
export const useCustomAttributesSettings = () => {
  return useQuery({
    queryKey: CUSTOM_ATTRIBUTES_SETTINGS_QUERY_KEY,
    queryFn: getCustomAttributesForUsers,
    staleTime: 5 * 60 * 1000,
    select: attributes =>
      attributes.filter(attr => SUPPORTED_CUSTOM_ATTRIBUTE_TYPES.has(attr.type)),
  });
};
